'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import NewsletterSection from '@/components/NewsletterSection';

const highlights = [
'60,000 barrels/day processing capacity',
'Produces petrol, diesel, jet fuel, and LPG',
"Reduces Uganda\'s dependence on imported fuel",
'Anchors downstream energy security',
'Located within Kabaale Industrial Park'];


const stats = [
{ value: '60K', label: 'Barrels/day capacity' },
{ value: '4+', label: 'Product types' },
{ value: '2028', label: 'Target completion' },
{ value: 'Planning', label: 'Current phase' }];


export default function RefineryPage() {
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    setHeroVisible(true);
  }, []);

  return (
    <div className="w-full min-h-screen bg-white flex flex-col">
      <Navbar />
      <main id="main-content" className="flex-1">
        {/* Hero */}
        <section className="bg-[#0F0F0F] pt-40 pb-20 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(204, 0, 0) 1px, transparent 0px)',
              backgroundSize: '40px 40px'
            }} />
          
          <div className="absolute top-0 right-0 w-1/3 h-full opacity-[0.03] pointer-events-none">
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-[#CC0000] blur-[120px] rounded-full" />
          </div>

          <div
            className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 relative"
            style={{
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? 'none' : 'translateY(20px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease'
            }}>
            
            <nav className="flex items-center gap-2 text-xs font-mono tracking-[0.15em] uppercase text-gray-500 mb-8" aria-label="Breadcrumb">
              <Link href="/" className="hover:text-[#CC0000] transition-colors">Home</Link>
              <span className="text-[#CC0000]">/</span>
              <Link href="/projects" className="hover:text-[#CC0000] transition-colors">Our Business</Link>
              <span className="text-[#CC0000]">/</span>
              <span className="text-[#CC0000]">Refinery</span>
            </nav>

            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 01 ]</span>
              <div className="h-[1px] w-12 bg-[#CC0000]" />
              <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em] font-heading">Downstream</span>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white font-heading leading-[1.05] tracking-tight">
                Uganda <span className="text-[#CC0000]">Oil Refinery</span>
              </h1>
              <span className="inline-flex items-center px-3 py-1.5 rounded-sm text-[10px] font-bold uppercase tracking-[0.15em] bg-gray-200 text-gray-700">
                Planning Phase
              </span>
            </div>

            <div className="flex items-center gap-2 text-gray-400 text-sm mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#CC0000]" aria-hidden="true">
                <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>Kabaale, Hoima District, Uganda</span>
            </div>

            <p className="text-lg text-gray-400 max-w-3xl leading-relaxed">
              The Uganda Oil Refinery will be a 60,000 barrels/day facility located within the Kabaale Industrial Park in Hoima District. It will process crude oil from the Lake Albert fields into petroleum products for domestic consumption and regional export.
            </p>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#CC0000] to-transparent opacity-50" />
        </section>

        {/* Featured Image */}
        <section className="bg-white pt-16">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="relative overflow-hidden bg-[#0F0F0F]">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_19e306e34-1770718255185.png"
                alt="Uganda Oil Refinery construction site at Kabaale Industrial Park in Hoima District downstream operations"
                className="w-full h-[320px] sm:h-[420px] lg:h-[500px] object-cover opacity-80" />
              
              <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F0F]/85 via-[#0F0F0F]/30 to-transparent pointer-events-none" />
              <div className="absolute top-6 left-6 w-8 h-8 border-l-2 border-t-2 border-[#CC0000]" />
              <div className="absolute bottom-6 right-6 w-8 h-8 border-r-2 border-b-2 border-[#CC0000]" />
              <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">/</span>
                  <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em] font-heading">Kabaale Industrial Park</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-extrabold font-heading max-w-2xl leading-[1.1] tracking-tight">
                  Refining Uganda&apos;s crude into national prosperity
                </h2>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-[#0F0F0F] py-16 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 divide-x divide-white/10">
              {stats?.map((stat, i) =>
              <div key={i} className="px-8 py-6 text-center first:pl-0 last:pr-0">
                  <div className="text-4xl sm:text-5xl font-extrabold text-white mb-2 font-heading tracking-tight">
                    {stat?.value}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-[0.15em]">{stat?.label}</div>
                  <div className="mt-3 h-[2px] w-8 bg-[#CC0000] mx-auto" />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Project Details */}
        <section className="py-24 bg-white border-t border-gray-200">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 02 ]</span>
                  <div className="h-[1px] w-12 bg-[#CC0000]" />
                  <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em] font-heading">Project Overview</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0F0F0F] mb-6 font-heading leading-[1.05] tracking-tight">
                  Anchoring <span className="text-[#CC0000]">energy security</span>
                </h2>
                <p className="text-gray-600 leading-relaxed mb-5 text-lg">
                  The Uganda Oil Refinery is a critical downstream project that will transform Uganda from a crude oil exporter into a country capable of processing its own petroleum products. Located within the Kabaale Industrial Park, the refinery will process 60,000 barrels of crude oil per day.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg">
                  The refinery will produce a range of petroleum products including petrol, diesel, jet fuel, kerosene, and LPG. This will significantly reduce Uganda&apos;s dependence on imported refined products, saving foreign exchange and strengthening national energy security.
                </p>

                <div className="mt-8 p-6 bg-[#F8F9FA] border border-gray-200">
                  <h4 className="text-sm font-bold text-[#0F0F0F] mb-4 font-heading uppercase tracking-[0.1em]">
                    <span className="text-[#CC0000] mr-2">→</span> Key Highlights
                  </h4>
                  <ul className="space-y-3">
                    {highlights?.map((h, i) =>
                    <li key={i} className="flex items-center gap-3 text-sm text-gray-700">
                        <span className="w-1.5 h-1.5 bg-[#CC0000] rounded-full flex-shrink-0" />
                        {h}
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-[#F8F9FA] p-8 border border-gray-200">
                  <h3 className="text-lg font-extrabold text-[#0F0F0F] mb-6 font-heading">Project Specifications</h3>
                  <div className="space-y-4">
                    {[
                    { label: 'Project Type', value: 'Oil Refinery (Downstream)' },
                    { label: 'Location', value: 'Kabaale, Hoima District' },
                    { label: 'Processing Capacity', value: '60,000 bbl/day' },
                    { label: 'Products', value: 'Petrol, Diesel, Jet Fuel, Kerosene, LPG' },
                    { label: 'Market', value: 'Domestic & Regional Export' },
                    { label: 'Status', value: 'Planning Phase' },
                    { label: 'Target Completion', value: '2028' }]?.
                    map((spec) =>
                    <div key={spec?.label} className="flex justify-between items-start py-3 border-b border-gray-200 last:border-0">
                        <span className="text-xs text-gray-500 uppercase tracking-[0.1em] font-heading">{spec?.label}</span>
                        <span className="text-sm text-[#0F0F0F] font-medium text-right max-w-[60%]">{spec?.value}</span>
                      </div>
                    )}
                  </div>
                </div>

                <Link
                  href="/projects"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#CC0000] hover:text-[#AA0000] transition-colors font-heading uppercase tracking-[0.1em]">
                  
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                  Back to Our Business
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <NewsletterSection />
      <Footer />
    </div>);

}